import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CypherEditor } from '../cypher/CypherEditor';
import { deriveColumnHeader, formatCellValue } from './formatResults';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';
import { sql } from '@codemirror/lang-sql';
import {
  Button,
  Card,
  Table,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

/** Read-only CodeMirror view for syntax-highlighted Cypher display */
const CypherReadOnly: React.FC<{ value: string }> = ({ value }) => {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const highlight = HighlightStyle.define([
      { tag: tags.keyword, color: theme.colors.semantic.info.default, fontWeight: '600' },
      { tag: tags.string, color: '#a3be8c' },
      { tag: tags.number, color: '#d08770' },
      { tag: tags.comment, color: theme.colors.text.secondary, fontStyle: 'italic' },
    ]);
    const state = EditorState.create({
      doc: value,
      extensions: [
        sql(),
        syntaxHighlighting(highlight),
        EditorState.readOnly.of(true),
        EditorView.editable.of(false),
        EditorView.theme({
          '&': { fontSize: '0.8rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace' },
          '.cm-content': { padding: spacing.xs },
          '.cm-editor': {
            border: 'none',
            background: theme.colors.background.elevated,
            borderRadius: '4px',
          },
          '.cm-focused': { outline: 'none' },
          '.cm-gutters': { display: 'none' },
          '.cm-cursor': { display: 'none' },
          '.cm-selectionBackground': { background: `${theme.colors.semantic.info.default}22` },
        }),
        EditorView.lineWrapping,
      ],
    });
    const view = new EditorView({ state, parent: containerRef.current });
    viewRef.current = view;
    return () => { view.destroy(); viewRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <div ref={containerRef} style={{ color: theme.colors.text.primary }} />;
};

interface ExplorerStepProps {
  stepNumber: number;
  reasoning: string;
  suggestedCypher: string;
  results: Record<string, unknown>[] | null;
  columns: string[];
  isExecuting: boolean;
  isComplete: boolean;
  isContinuing?: boolean;
  isLastStep?: boolean;
  isInvestigationComplete?: boolean;
  generatingReport?: boolean;
  onExecute: (cypher: string) => void;
  onContinue: () => void;
  onStop: () => void;
  onGenerateReport?: () => void;
  onHighlightStep?: () => void;
}

export const ExplorerStep: React.FC<ExplorerStepProps> = ({
  stepNumber, reasoning, suggestedCypher, results, columns, isExecuting, isComplete,
  isContinuing, isLastStep, isInvestigationComplete, generatingReport,
  onExecute, onContinue, onStop, onGenerateReport, onHighlightStep,
}) => {
  const { theme } = useTheme();
  const [editedCypher, setEditedCypher] = useState(suggestedCypher);
  const [showResults, setShowResults] = useState(false);

  const hasResults = results !== null;
  const isStepDone = isComplete && hasResults;

  // Formatted columns with descriptive headers and formatted cell values
  const resultColumns = useMemo(() => {
    if (!results || columns.length === 0) return [];
    return columns.map((col) => ({
      key: col,
      header: deriveColumnHeader(col, results),
      render: (value: unknown) => {
        const formatted = formatCellValue(value);
        if (formatted.includes('\n')) {
          return (
            <pre style={{
              margin: 0,
              fontSize: '0.78rem',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: theme.colors.text.primary,
              lineHeight: 1.4,
            }}>
              {formatted}
            </pre>
          );
        }
        return <span style={{ fontSize: '0.8rem' }}>{formatted}</span>;
      },
    }));
  }, [results, columns, theme]);

  return (
    <Card>
      <Card.Body>
        <Stack gap="sm">
          {/* Step header */}
          <Flex align="center" gap="sm">
            <div style={{
              width: 24, height: 24, borderRadius: '50%',
              background: isStepDone ? theme.colors.semantic.success.default
                : isInvestigationComplete ? theme.colors.semantic.info.default
                : theme.colors.semantic.info.default,
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
            }}>
              {stepNumber}
            </div>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.colors.text.primary }}>
              Step {stepNumber}
            </span>
          </Flex>

          {/* AI Reasoning */}
          <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {reasoning}
          </div>

          {/* INVESTIGATION COMPLETE: show report button instead of query/results */}
          {isInvestigationComplete && onGenerateReport && (
            <Flex justify="end" gap="sm">
              <Button variant="solid" color="primary" size="sm" onClick={onGenerateReport} disabled={generatingReport}>
                {generatingReport ? 'Generating...' : 'Generate Report'}
              </Button>
            </Flex>
          )}

          {/* Cypher query — syntax-highlighted read-only for completed steps, editor for active step */}
          {suggestedCypher && !isInvestigationComplete && (
            isStepDone ? (
              <CypherReadOnly value={suggestedCypher} />
            ) : (
              <CypherEditor
                value={editedCypher}
                onChange={setEditedCypher}
                onExecute={() => onExecute(editedCypher)}
                executing={isExecuting}
                error={null}
              />
            )
          )}

          {/* Collapsible results table */}
          {results && results.length > 0 && (
            <div>
              <Flex align="center" gap="xs">
                <Button variant="ghost" size="sm" onClick={() => setShowResults(!showResults)}
                  style={{ fontSize: '0.8rem', padding: '2px 6px' }}>
                  {showResults ? '▾' : '▸'} {results.length} result{results.length !== 1 ? 's' : ''}
                </Button>
                {onHighlightStep && (
                  <Button variant="outline" size="sm" onClick={onHighlightStep}
                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}>
                    Highlight
                  </Button>
                )}
              </Flex>
              {showResults && (
                <div style={{ maxHeight: '250px', overflow: 'auto', marginTop: spacing.xs }}>
                  <Table data={results} columns={resultColumns} bordered emptyMessage="No results" />
                </div>
              )}
            </div>
          )}
          {results && results.length === 0 && (
            <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, padding: spacing.xs }}>
              No results returned.
            </div>
          )}

          {/* Continue/Stop — only on the last completed step that isn't investigation complete */}
          {isComplete && hasResults && isLastStep && !isInvestigationComplete && (
            <Flex justify="end" gap="sm">
              <Button variant="outline" size="sm" onClick={onStop} disabled={isContinuing}>Stop</Button>
              <Button variant="solid" color="primary" size="sm" onClick={onContinue} disabled={isContinuing}>
                {isContinuing ? 'Analyzing...' : 'Continue'}
              </Button>
            </Flex>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
};
